import FilterBar, { defaultFilters, MakeFilters } from './FilterBarMake';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import useTablePages from '../../../../hooks/useTablePages';
import { GET_MAKE_IDS, GET_MAKES } from './Makes.query';
import React from 'react';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import availableColumns from './Makes.columns';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';
import useMode from '../../../../hooks/useMode';
import { Flex } from 'rebass';
import PageBar from '../../../../components/PageBar/PageBar';
import TableWindow from '../../../../components/Table/TableWindow';
import { PortalWithState } from 'react-portal';
import SimpleAddModal from '../../../../components/Modal/SimpleAddModal';
import { useMutation } from '@apollo/client';
import useNotify from '../../../../hooks/useNotify';
import { getStringSearchOr } from '../../../../helpers/search';
import { gql } from '../../gql';

const stringSearchFields = [
  {
    field: 'name',
    label: 'Name',
  },
];

const CREATE_MAKE = gql(`
  mutation CreateMake($input: BackflowAssemblyMakeInput!) {
    createBackflowAssemblyMake(input: $input) {
      id
      name
    }
  }
`);

type MakesProps = {
  makeId?: string;
};

/**
 * @component Used for makes page
 */
function Makes({ makeId }: MakesProps): JSX.Element {
  const convertStringSearch = (stringSearch?: string) => ({
    or: getStringSearchOr(stringSearchFields, stringSearch),
  });

  const convertFilters = (filters: MakeFilters) => {
    return {
      ...convertStringSearch(filters.makeName),
    };
  };

  const { showError, showSuccess } = useNotify();

  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<MakeFilters>(convertFilters, 'makes', defaultFilters);

  const { orderBy, onHeaderClick } = useOrderBy(
    'makes',
    availableColumns,
    'name'
  );

  const {
    loading,
    error,
    itemIds,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages(
    'backflowAssemblyMakes',
    GET_MAKE_IDS,
    {
      where: queryFilters,
      limit: null,
      orderBy,
    },
    GET_MAKES,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    })
  );

  const [createMake] = useMutation(CREATE_MAKE, {
    onCompleted: refetch,
    onError: (error) => {
      showError('Error creating make', error);
    },
  });

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'makes');

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(`/${useMode()}/makes`, itemIds, makeId);

  return (
    <Flex flexDirection="column" flexGrow={1}>
      <PageBar title="Makes" />
      <PortalWithState closeOnEsc>
        {({ openPortal, closePortal, portal }) => (
          <>
            <FilterBar
              filters={storedFilters}
              setFilters={setStoredFilters}
              selectedId={makeId}
              onAdd={openPortal}
            />
            {portal(
              <SimpleAddModal
                title="Add Make"
                placeholder="make name"
                onClose={closePortal}
                onSave={async (value) => {
                  const result = await createMake({
                    variables: {
                      input: {
                        name: value,
                      },
                    },
                  });
                  onRowClick(result.data?.createBackflowAssemblyMake);
                  closePortal();
                }}
              />
            )}
          </>
        )}
      </PortalWithState>
      {loading && itemIds.length === 0 ? (
        'Loading...'
      ) : (
        <TableWindow
          emptyMessage="No makes found."
          columns={visibleColumns}
          itemIds={itemIds}
          itemLoader={itemLoader}
          onSelectionChange={setSelectedIds}
          selectedIds={selectedIds}
          activeIds={makeId ? [makeId] : undefined}
          onHeaderClick={onHeaderClick}
          orderBy={orderBy}
          onRowClick={onRowClick}
          columnConfiguration={columnConfiguration}
          setColumnConfiguration={setColumnConfiguration}
        />
      )}
    </Flex>
  );
}

export default Makes;
